import { ActionButton, ActionButtonType, Drawer } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { LiveStatusSummaryResource } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import React, { useState } from "react";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { Timeline } from "./Timeline/Timeline";
interface DeploymentTimelineDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    deploymentName: string;
    environmentId: string;
    projectId: string;
    tenantId?: string;
    spaceId: string;
}
const pageSize = 30;
// Inner component that only executes when the drawer is open
const DeploymentTimelineDrawerInner = (props: Omit<DeploymentTimelineDrawerProps, "isOpen" | "onClose">) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [refreshLiveStatus, setRefreshLiveStatus] = useState(true);
    const timelineResponse = useInlineStatusQuery((repository) => {
        return repository.Deployments.getDeploymentTimeline(props.projectId, props.environmentId, props.tenantId, 0, (currentPage + 1) * pageSize);
    }, [props.projectId, props.environmentId, props.tenantId, currentPage], "Timeline Data Query", {
        refetchIntervalInMs: 3000, // Always refresh since this component only renders when drawer is open
    }).result;
    const isKubernetesLiveStatusEnabled = useOctopusFeatureToggle("kubernetes-monitor", false) && isFeatureToggleEnabled("KubernetesLiveObjectStatusFeatureToggle");
    const liveStatus = useInlineStatusQuery(async (repo): Promise<LiveStatusSummaryResource> => {
        const isSelfHosted = (await repo.Licenses.getCurrentStatus()).HostingEnvironment === HostingEnvironment.SelfHosted;
        const isRunningInHACluster = isSelfHosted && (await repo.OctopusServerNodes.all()).length > 1;
        if (isKubernetesLiveStatusEnabled && !isRunningInHACluster) {
            const response = await repo.KubernetesLiveStatus.getLiveStatus(props.projectId, props.environmentId, props.tenantId, true);
            setRefreshLiveStatus(response.Summary.Status !== "NotSupported");
            return response.Summary;
        }
        else {
            // When Live Status is not enabled, hardcode a "NotSupported" response
            setRefreshLiveStatus(false);
            return {
                Status: "NotSupported",
                LastUpdated: moment(),
            };
        }
    }, [isKubernetesLiveStatusEnabled, props.projectId, props.environmentId, props.tenantId], "Live Status Rollup", {
        refetchIntervalInMs: refreshLiveStatus ? 3000 : undefined, // Always refresh when supported since drawer is open
    }).result;
    const loadMore = () => {
        setCurrentPage((prev) => prev + 1);
    };
    const items = timelineResponse?.TimelineItems.Items ?? [];
    const hasMore = (timelineResponse?.TimelineItems?.TotalResults ?? 0) > (timelineResponse?.TimelineItems?.Items?.length ?? 0);
    return <Timeline {...props} liveStatusSummary={liveStatus} timelineData={items} loadMore={loadMore} hasMore={hasMore}/>;
};
export const DeploymentTimelineDrawer = (props: DeploymentTimelineDrawerProps) => {
    // Lightweight query just for environment name - only fetches once
    const { result: environment } = useInlineStatusQuery((repository) => repository.Environments.get(props.environmentId), [props.environmentId], "Environment Name Query");
    const environmentName = environment ? environment.Name : "Unknown";
    return (<Drawer isOpen={props.isOpen} onClose={() => {
            return;
        }} title="Deployment Timeline" subtitle={`${props.deploymentName} deployments to ${environmentName}`} variant="customActions" layout="basic" customActions={[<ActionButton onClick={props.onClose} type={ActionButtonType.Secondary} label="Close"/>]} width="narrow">
            <DeploymentTimelineDrawerInner deploymentName={props.deploymentName} environmentId={props.environmentId} projectId={props.projectId} tenantId={props.tenantId} spaceId={props.spaceId}/>
        </Drawer>);
};
